import { Box, chakra, Flex, Text } from "@chakra-ui/react";
import React, { ReactNode, useEffect, useMemo, useState } from "react";
import { getAccelerateProducts } from "../request";
import { ObjKeysCamelFormat } from "@/utils/variableName";
import QRcodeCard from "../components/QrcodeCard";
import { accelerationStore } from "@/stores/acceleration";
import { formatTime } from "@/utils/format-time";
import { observer } from "mobx-react-lite";
import PaySuccessModal from "../components/PaySuccessModal";
import config from "@/config";
import { ONE_DAY_MILLISECONDS } from "@/utils/constants";
import { getGlobalPending } from "@/modules/editor/request/generator-task";
import NewPayModal from "../components/NewPayModal";
import NewPriceCard from "../components/NewPriceCard";

interface ProductInfoItem {
  activeDesc: string;
  activePrice: string;
  createdAt: string;
  id: number;
  name: string;
  period: number;
  price: string;
  unitPrice: string;
  isUsed: boolean;
}

const AcceleratePayment: React.FC<{
  open?: boolean;
  onClose?: () => void;
  onPaySuccess?: () => void;
}> = ({
  open = false,
  onClose,
  onPaySuccess = async () => {
    onClose?.();
    accelerationStore.afterPayment();
  },
}) => {
  const {
    data: { expireTime },
    isVip,
  } = accelerationStore;

  const [pendingMaximum, setPendingMaximum] = useState(0);

  const [paidModalOpen, setPaidModalOpen] = useState(false);

  const [products, setProducts] = useState<ProductInfoItem[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<ProductInfoItem | null>(null);

  useEffect(() => {
    if (open) {
      getGlobalPending().then((value) => {
        setPendingMaximum(value ?? 0);
      });
    }
  }, [open]);

  useEffect(() => {
    getAccelerateProducts()
      .then((res) => res)
      .then((res) => {
        if (res.code === 0) {
          const data = ObjKeysCamelFormat(res.data);
          const products: ProductInfoItem[] = [];
          for (let i = 0; i < data.productInfo.length; i++) {
            const info = data.productInfo[i] as ProductInfoItem;
            if (!info.isUsed) products.push(info);
            if (info.period === 30 || info.period === 31) setSelectedProduct(info);
          }
          setProducts(products);
        }
      });
  }, [isVip]);

  function handleClose() {
    onClose?.();
  }

  async function handlePaidSuccess() {
    await onPaySuccess?.();

    setPaidModalOpen(true);
  }

  const expireTimeAfterPaid = useMemo(() => {
    const currentExpireTime = expireTime || new Date().getTime();
    if (selectedProduct) {
      const newExpireTime = currentExpireTime + selectedProduct.period * ONE_DAY_MILLISECONDS;
      return newExpireTime;
    }
    return currentExpireTime;
  }, [selectedProduct, expireTime]);

  const getPeriodText = (period: number) => {
    switch (period) {
      case 30:
      case 31:
        return " / 月";
      case 365:
      case 366:
        return " / 年";
      default:
        return "";
    }
  };

  const getUnitInfo = (period: number, price: number) => {
    switch (period) {
      case 1:
      case 2:
        return "仅 24h 内无限生成";
      case 30:
      case 31:
        return `每天不到 ${Math.ceil(price / period)} 元钱`;
      case 365:
      case 366:
        return `每天约 ${(price / period).toFixed(1)} 元`;
      default:
        return "";
    }
  };

  const getProductName = (period: number, name: string) => {
    switch (period) {
      case 1:
      case 2:
        return "当日会员试用";
      case 30:
      case 31:
        return "单月会员";
      case 365:
      case 366:
        return "全年会员";
      default:
        return name;
    }
  };

  const redirectURI = useMemo(() => {
    return `${config.creatorMobileURL}/mobile_payment?type=nolibox`;
  }, []);
  return (
    <>
      <NewPayModal
        isVip={isVip}
        isOpen={open}
        onClose={handleClose}
        expire={expireTime ? expireTime * 1000 : expireTimeAfterPaid}
        qrCode={
          <QRcodeCard
            productId={selectedProduct?.id}
            amount={Number(selectedProduct?.activePrice)}
            isYuan
            onSuccess={handlePaidSuccess}
            redirectUri={redirectURI}
          />
        }
      >
        {products.map((product) => {
          const { period, price, id, name, activePrice } = product;
          const selected = selectedProduct?.id === id;
          return (
            <Box key={id} w="143px" mr="12px" flexGrow={1}>
              <NewPriceCard
                title={getProductName(period, name)}
                onClick={() => {
                  setSelectedProduct(product);
                }}
                selected={selected}
                curPrice={activePrice}
                periodPrice={getPeriodText(period)}
                unitInfo={getUnitInfo(period, Number(activePrice))}
                oldPrice={price}
              ></NewPriceCard>
            </Box>
          );
        })}
      </NewPayModal>
      <PaySuccessModal
        isOpen={paidModalOpen}
        onClose={() => setPaidModalOpen(false)}
        onBtnClick={() => setPaidModalOpen(false)}
        title="支付成功"
        info={`画宇宙会员有效期截至：${formatTime(
          expireTime ? expireTime * 1000 : expireTimeAfterPaid,
          "YYYY-MM-DD",
        )}`}
        imageDesc="你已经在使用画宇宙会员权益"
        footerText="使用中有任何问题可通过微信联系客服"
      />
    </>
  );
};

export default observer(AcceleratePayment);
